import React, { useEffect, useState } from 'react';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import { Col, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import InputLabel from 'components/layout/forms/InputLabel';
import NumberWithUnitInputField from 'components/layout/forms/NumberWithUnitInputField';
import translations from 'services/translations/translations';

import { useCurrency } from 'services/offers/currency';
import {
  convertMoney,
  currencySymbol,
  MoneyCurrency,
  useExchangeRate,
} from 'services/offers/exchangeRate';
import CurrencyToggle from '../../CurrencyToggle';
import getPriceSummary from '../getPriceSummary';
import { getMarginClass } from './getMarginRatioClassName';
import IInstallation from '../IInstallation';
import ISubproduct from '../ISubproduct';

const Summary = ({
  installations,
  productUnit,
  quantity,
  subproducts,
}: {
  installations: IInstallation[];
  productUnit: string;
  quantity: number;
  subproducts: ISubproduct[];
}): JSX.Element => {
  const { t } = useTranslation([translations.offers]);

  // The summary can be viewed in the other currency at the rate of today; the rows and
  // the saved prices stay in the currency of the offer
  const base: MoneyCurrency = useCurrency() === 'SEK' ? 'SEK' : 'EUR';
  const [shown, setShown] = useState<MoneyCurrency>(base);
  useEffect(() => setShown(base), [base]);
  const rate = useExchangeRate();
  const currency = currencySymbol(shown);
  const money = (value: number) =>
    formatGroupedNumber(convertMoney(value, base, shown, rate));

  const priceSummary = getPriceSummary(installations, subproducts, quantity);

  return (
    <div className="calculation-sum-row">
      <div className="calculation-sum-header">
        <h2>{t(`${translations.calculationWindow}:Calculation`)}</h2>
        <CurrencyToggle
          base={base}
          onChange={setShown}
          rate={rate}
          value={shown}
        />
      </div>
      {/* Selling price first, then expense and margin: per unit, totals, margin % */}
      <Row>
        <Col className="key-figure">
          <InputLabel>
            {t(`${translations.offers}:Selling price`)} {currency}/
            {t(`${translations.common}:units:${productUnit}`)}
          </InputLabel>
        </Col>
        <Col>
          <InputLabel>
            {t(`${translations.offers}:Expense`)} {currency}/
            {t(`${translations.common}:units:${productUnit}`)}
          </InputLabel>
        </Col>
        <Col className="key-figure">
          <InputLabel>
            {`${t(`${translations.offers}:Contribution margin`)} ${currency}`}/
            {t(`${translations.common}:units:${productUnit}`)}
          </InputLabel>
        </Col>
        <Col>
          <InputLabel>{t(`${translations.offers}:Selling price`)}</InputLabel>
        </Col>
        <Col>
          <InputLabel>{t(`${translations.offers}:Expense`)}</InputLabel>
        </Col>
        <Col>
          <InputLabel>
            {`${t(`${translations.offers}:Contribution margin`)} ${currency}`}
          </InputLabel>
        </Col>
        <Col className="key-figure">
          <InputLabel>
            {`${t(`${translations.offers}:Contribution margin`)} %`}
          </InputLabel>
        </Col>
      </Row>
      <Row>
        <Col className="key-figure">
          <NumberWithUnitInputField
            disabled
            id="priceForUnit"
            unit={`${currency}/${t(
              `${translations.common}:units:${productUnit}`
            )}`}
            value={money(priceSummary.sellingPriceForUnit)}
          />
        </Col>
        <Col>
          <NumberWithUnitInputField
            disabled
            id="expense-sum-for-unit"
            unit={`${currency}/${t(
              `${translations.common}:units:${productUnit}`
            )}`}
            value={money(priceSummary.expenseSumForUnit)}
          />
        </Col>
        <Col className="key-figure">
          <NumberWithUnitInputField
            disabled
            id="contribution-margin-for-unit"
            unit={`${currency}/${t(
              `${translations.common}:units:${productUnit}`
            )}`}
            value={money(priceSummary.shownContributionMarginForUnit)}
          />
        </Col>
        <Col>
          <NumberWithUnitInputField
            disabled
            id="summary-price-sum"
            unit={currency}
            value={money(priceSummary.sellingPrice)}
          />
        </Col>
        <Col>
          <NumberWithUnitInputField
            disabled
            id="summary-expense-sum"
            unit={currency}
            value={money(priceSummary.expenseSum)}
          />
        </Col>
        <Col>
          <NumberWithUnitInputField
            disabled
            id="summary-contribution-margin-sum"
            unit={currency}
            value={money(priceSummary.shownContributionMargin)}
          />
        </Col>
        {/* Below 20 % red, above 30 % green, as on the rows */}
        <Col
          className={`key-figure ${
            priceSummary.sellingPrice === 0
              ? ''
              : getMarginClass(priceSummary.contributionMarginRatio)
          }`}
        >
          <NumberWithUnitInputField
            disabled
            id="summary-contribution-margin-ratio-sum"
            unit="%"
            value={formatGroupedNumber(priceSummary.contributionMarginRatio)}
          />
        </Col>
      </Row>
    </div>
  );
};

export default Summary;
